import { apiClient, pathSegment } from "@/lib/api/client";
import type { DocumentFileType } from "@/types/common";

/**
 * What an advert requires of an applicant, and how the student stands on it.
 *
 * The student's own documents live in `document-service` — that is the library,
 * and the only place they are managed. This module answers the other question:
 * for *this* home, what does the landlord ask for. The two meet in the dialog
 * shown when applying, which is where a library document gets connected to a
 * landlord.
 */

export type DocumentReviewStatus =
  | "NOT_REVIEWED"
  | "APPROVED"
  | "REJECTED"
  | "UNKNOWN";

/** One document an advert asks for, and where the student stands on it. */
export type ListingRequiredDocument = {
  caption: string;
  /** False means the student may apply without it. */
  mandatory: boolean;
  validityDays: number | null;
  allowedTypes: Array<DocumentFileType | string>;
  /** Null when the requirement's caption matches no type in the company's catalogue. */
  documentRequestTypeId: string | null;
  /** What the landlord already holds, if anything. */
  filedDocumentId: string | null;
  expiresAt: string | null;
  reviewStatus: DocumentReviewStatus | null;
  satisfied: boolean;
  expired: boolean;
};

export type ListingApplicationDocuments = {
  companyId: number | null;
  companyName: string | null;
  /** False when the student has never been registered with this landlord. */
  applicantKnown: boolean;
  reachable: boolean;
  documents: ListingRequiredDocument[];
};

export type LandlordDocumentsCompany = {
  companyId: number;
  companyName: string | null;
  companyLogoUrl: string | null;
};

const BASE = "/me/landlord-documents";

export const landlordDocumentService = {
  /** What an advert requires, for the dialog shown when applying. */
  getForListing: (listingId: string, signal?: AbortSignal) =>
    apiClient<ListingApplicationDocuments>(
      `${BASE}/listing/${pathSegment(listingId)}`,
      { signal }
    ),
};
